import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createTheme, ThemeProvider, CssBaseline, Box, Container, Typography, Button, Card, CardContent, Grid, AppBar, Toolbar, Link, IconButton, TextField, FormControlLabel, Checkbox, Radio, RadioGroup, FormControl, FormLabel, Select, MenuItem, Chip, Divider, Alert, CircularProgress, LinearProgress } from '@mui/material';

function ThemePreview() {
    const { clinicId } = useParams();
    const [theme, setTheme] = useState(null);

    useEffect(() => {
        // Load theme from localStorage initially
        const previewTheme = localStorage.getItem('previewTheme');
        if (previewTheme) {
            setTheme(JSON.parse(previewTheme));
        }

        // Set up BroadcastChannel to receive real-time updates
        const channel = new BroadcastChannel('theme-preview');
        channel.onmessage = (event) => {
            if (event.data.type === 'THEME_UPDATE') {
                setTheme(event.data.theme);
            }
        };

        return () => {
            channel.close();
        };
    }, [clinicId]);

    if (!theme) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6">No theme settings found</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Please configure the theme settings in the admin panel first.
                </Typography>
            </Box>
        );
    }

    // Helper function to convert rem to pixels
    const remToPx = (remValue) => {
        if (typeof remValue === 'string' && remValue.endsWith('rem')) {
            return parseFloat(remValue) * 16;
        }
        return parseFloat(remValue) || 14;
    };

    // Create Material UI theme from custom theme settings
    const muiTheme = createTheme({
        palette: {
            mode: theme.theme.mode === 'auto' ? 'light' : theme.theme.mode,
            primary: {
                main: theme.theme.colors.primary,
                light: theme.theme.colors.primaryLight,
                dark: theme.theme.colors.primaryDark,
            },
            secondary: {
                main: theme.theme.colors.secondary,
                light: theme.theme.colors.secondaryLight,
            },
            success: {
                main: theme.theme.colors.success,
            },
            info: {
                main: theme.theme.colors.info,
            },
            warning: {
                main: theme.theme.colors.warning,
            },
            error: {
                main: theme.theme.colors.danger,
            },
            background: {
                default: theme.theme.colors.background,
                paper: theme.theme.colors.surface,
            },
            text: {
                primary: theme.theme.colors.text,
                secondary: theme.theme.colors.textMuted,
            },
        },
        typography: {
            fontFamily: theme.theme.fonts.primary,
            fontSize: remToPx(theme.theme.fonts.size.base),
            h1: {
                fontSize: remToPx(theme.theme.fonts.size.xl) * 1.5,
            },
            h2: {
                fontSize: remToPx(theme.theme.fonts.size.xl),
            },
            h3: {
                fontSize: remToPx(theme.theme.fonts.size.large),
            },
            h4: {
                fontSize: remToPx(theme.theme.fonts.size.large) * 0.85,
            },
            h5: {
                fontSize: remToPx(theme.theme.fonts.size.base) * 1.25,
            },
            h6: {
                fontSize: remToPx(theme.theme.fonts.size.base),
            },
            body1: {
                fontSize: remToPx(theme.theme.fonts.size.base),
            },
            body2: {
                fontSize: remToPx(theme.theme.fonts.size.small),
            },
        },
        shape: {
            borderRadius: parseInt(theme.theme.borderRadius) || 8,
        },
    });

    const Header = () => {
        const headerStyles = {
            backgroundColor: theme.header.backgroundColor === 'auto' 
                ? muiTheme.palette.primary.main 
                : theme.header.backgroundColor,
            color: theme.header.textColor,
            borderBottom: theme.header.showBorder ? `1px solid ${theme.theme.colors.border}` : 'none',
            position: theme.header.sticky ? 'sticky' : 'relative',
            top: 0,
            zIndex: 1100,
        };

        const logoStyles = {
            height: theme.header.logoHeight,
            width: 'auto',
            objectFit: 'contain',
        };

        const clinicNameStyles = {
            fontSize: { xs: '1.25rem', sm: remToPx(theme.header.clinicNameSize) },
            fontWeight: 'bold',
        };

        const getFlexAlignment = (position) => {
            switch (position) {
                case 'center': return 'center';
                case 'right': return 'flex-end';
                default: return 'flex-start';
            }
        };

        return (
            <AppBar position="static" sx={headerStyles} elevation={0}>
                <Container maxWidth="lg">
                    <Toolbar sx={{ 
                        py: theme.header.padding.top, 
                        pb: theme.header.padding.bottom, 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'center', sm: 'center' },
                        gap: { xs: 2, sm: 0 }
                    }}>
                        
                        {/* Left Section */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: { xs: getFlexAlignment(theme.header.logoPositionMobile), sm: getFlexAlignment(theme.header.logoPosition) },
                            flex: 1,
                            width: { xs: '100%', sm: 'auto' }
                        }}>
                            {theme.header.showLogo && theme.logo.url && (
                                <Box 
                                    component="img"
                                    src={theme.logo.url}
                                    alt={theme.logo.alt}
                                    sx={{ 
                                        height: { xs: theme.header.logoHeightMobile, sm: theme.header.logoHeight },
                                        width: 'auto',
                                        objectFit: 'contain',
                                        maxWidth: '100%'
                                    }}
                                />
                            )}
                            {theme.header.showClinicName && (
                                <Typography variant="h6" sx={{ ml: theme.header.showLogo && theme.logo.url ? 2 : 0, ...clinicNameStyles }}>
                                    {theme.clinicInfo.name}
                                </Typography>
                            )}
                        </Box>

                        {/* Right Section - Contact Info and Social Icons */}
                        {(theme.header.showContactInfo || theme.header.showSocialIcons) && (
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'row', 
                                alignItems: { xs: 'center', sm: theme.header.contactInfoPosition === 'left' ? 'flex-start' : 'flex-end' },
                                ml: { xs: 0, sm: 2 },
                                gap: 2,
                                display: { xs: (theme.header.showContactInfoMobile || theme.header.showSocialIconsMobile) ? 'flex' : 'none', sm: 'flex' }
                            }}>
                                {/* Social Icons - Left of Contact Info */}
                                {theme.header.showSocialIcons && (theme.header.socialIconsPosition === 'contactLeft' || theme.header.socialIconsPosition === 'left') && (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 1,
                                        alignItems: 'center'
                                    }}>
                                        {theme.clinicInfo.socialMedia?.map((social, index) => (
                                            <Box 
                                                key={index}
                                                sx={{ 
                                                    width: 32, 
                                                    height: 32, 
                                                    borderRadius: '50%',
                                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ fontSize: 14 }}>
                                                    {social.type === 'FACEBOOK' ? 'f' : 
                                                     social.type === 'INSTAGRAM' ? 'i' : 
                                                     social.type === 'TWITTER' ? 't' : 
                                                     social.type === 'LINKEDIN' ? 'in' : 's'}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {/* Contact Info */}
                                {theme.header.showContactInfo && (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        alignItems: { xs: 'center', sm: theme.header.contactInfoPosition === 'left' ? 'flex-start' : 'flex-end' },
                                        fontSize: { xs: '0.75rem', sm: remToPx(theme.header.contactInfoTextSize) },
                                        color: theme.header.contactInfoTextColor,
                                        textAlign: { xs: 'center', sm: theme.header.contactInfoPosition === 'left' ? 'left' : 'right' }
                                    }}>
                                        {theme.header.showAddress && (
                                            <Typography variant="body2" sx={{ mb: 0.5, color: 'inherit', fontSize: 'inherit' }}>
                                                {theme.clinicInfo.address.street}, {theme.clinicInfo.address.city}
                                            </Typography>
                                        )}
                                        {theme.header.showPhone && (
                                            <Typography variant="body2" sx={{ mb: 0.5, color: 'inherit', fontSize: 'inherit' }}>
                                                {theme.clinicInfo.contact.phone}
                                            </Typography>
                                        )}
                                        {theme.header.showFax && (
                                            <Typography variant="body2" sx={{ color: 'inherit', fontSize: 'inherit' }}>
                                                Fax: {theme.clinicInfo.contact.fax || 'N/A'}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {/* Social Icons - Right of Contact Info */}
                                {theme.header.showSocialIcons && (theme.header.socialIconsPosition === 'contactRight' || theme.header.socialIconsPosition === 'right') && (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 1,
                                        alignItems: 'center'
                                    }}>
                                        {theme.clinicInfo.socialMedia?.map((social, index) => (
                                            <Box 
                                                key={index}
                                                sx={{ 
                                                    width: 32, 
                                                    height: 32, 
                                                    borderRadius: '50%',
                                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ fontSize: 14 }}>
                                                    {social.type === 'FACEBOOK' ? 'f' : 
                                                     social.type === 'INSTAGRAM' ? 'i' : 
                                                     social.type === 'TWITTER' ? 't' : 
                                                     social.type === 'LINKEDIN' ? 'in' : 's'}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>
        );
    };

    const Footer = () => {
        const footerStyles = {
            backgroundColor: theme.footer.backgroundColor === 'auto' 
                ? muiTheme.palette.secondary.main 
                : theme.footer.backgroundColor,
            color: theme.footer.textColor,
            py: 4,
            mt: 'auto',
        };

        return (
            <Box sx={footerStyles}>
                <Container maxWidth="lg">
                    {theme.footer.layout === 'detailed' && (
                        <Grid container spacing={4} justifyContent="space-between">
                            <Grid item xs={12} md={4} sx={{ textAlign: 'left' }}>
                                <Typography variant="h6" gutterBottom>
                                    {theme.clinicInfo.name}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    {theme.clinicInfo.tagline}
                                </Typography>
                                {theme.footer.showContactInfo && (
                                    <Box>
                                        <Typography variant="body2">
                                            {theme.clinicInfo.address.street}
                                        </Typography>
                                        <Typography variant="body2">
                                            {theme.clinicInfo.address.city}, {theme.clinicInfo.address.province} {theme.clinicInfo.address.postalCode}
                                        </Typography>
                                        <Typography variant="body2">
                                            {theme.clinicInfo.contact.phone}
                                        </Typography>
                                        <Typography variant="body2">
                                            {theme.clinicInfo.contact.email}
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={12} md={4} sx={{ textAlign: 'left' }}>
                                <Typography variant="h6" gutterBottom>
                                    Quick Links
                                </Typography>
                                {theme.footer.showLinks && theme.footer.links.map((link, index) => (
                                    <Link 
                                        key={index} 
                                        href={link.url} 
                                        color="inherit" 
                                        display="block" 
                                        sx={{ mb: 1, fontSize: remToPx(theme.footer.quickLinksFontSize) }}
                                    >
                                        {link.text}
                                    </Link>
                                ))}
                            </Grid>
                            <Grid item xs={12} md={4} sx={{ textAlign: 'left' }}>
                                <Typography variant="h6" gutterBottom>
                                    Hours
                                </Typography>
                                {theme.footer.showClinicHours && (
                                    <Box>
                                        <Typography variant="body2">Mon-Fri: 9:00 AM - 6:00 PM</Typography>
                                        <Typography variant="body2">Saturday: 9:00 AM - 2:00 PM</Typography>
                                        <Typography variant="body2">Sunday: Closed</Typography>
                                    </Box>
                                )}
                                {theme.footer.showSocialIcons && (
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                        {theme.clinicInfo.socialMedia?.map((social, index) => (
                                            <Box 
                                                key={index}
                                                sx={{ 
                                                    width: 32, 
                                                    height: 32, 
                                                    borderRadius: '50%',
                                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ fontSize: 14 }}>
                                                    {social.type === 'FACEBOOK' ? 'f' : 
                                                     social.type === 'INSTAGRAM' ? 'i' : 
                                                     social.type === 'TWITTER' ? 't' : 
                                                     social.type === 'LINKEDIN' ? 'in' : 's'}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    )}
                    <Box sx={{ mt: 4, pt: 2, borderTop: `1px solid ${theme.theme.colors.border}` }}>
                        <Typography variant="body2" align="center">
                            {theme.footer.text}
                        </Typography>
                        {theme.footer.showPoweredBy && (
                            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                                {theme.footer.poweredByText}
                            </Typography>
                        )}
                    </Box>
                </Container>
            </Box>
        );
    };

    return (
        <ThemeProvider theme={muiTheme}>
            <CssBaseline />
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <Header />
                <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
                    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
                        <Typography variant="h3" gutterBottom align="center" sx={{ mb: 2, fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>
                            Theme Components Preview
                        </Typography>
                        <Typography variant="body1" paragraph align="center" color="text.secondary" sx={{ mb: 6, px: { xs: 2, sm: 0 } }}>
                            Preview of various UI components with your theme settings
                        </Typography>

                        {/* Typography Section */}
                        <Card sx={{ mb: 4 }}>
                            <CardContent sx={{ px: { xs: 2, sm: 3 } }}>
                                <Typography variant="h5" gutterBottom>
                                    Typography
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Typography variant="h1" gutterBottom sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }}>
                                    Heading 1
                                </Typography>
                                <Typography variant="h2" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2rem' } }}>
                                    Heading 2
                                </Typography>
                                <Typography variant="h3" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
                                    Heading 3
                                </Typography>
                                <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                                    Heading 4
                                </Typography>
                                <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                                    Heading 5
                                </Typography>
                                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}>
                                    Heading 6
                                </Typography>
                                <Typography variant="body1" paragraph>
                                    Body 1 text - Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    Body 2 text - Smaller body text for secondary information.
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Caption text - For small labels and metadata.
                                </Typography>
                            </CardContent>
                        </Card>

                        {/* Buttons Section */}
                        <Card sx={{ mb: 4 }}>
                            <CardContent sx={{ px: { xs: 2, sm: 3 } }}>
                                <Typography variant="h5" gutterBottom>
                                    Buttons
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Contained Buttons
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Button variant="contained">Default</Button>
                                        <Button variant="contained" color="primary">Primary</Button>
                                        <Button variant="contained" color="secondary">Secondary</Button>
                                        <Button variant="contained" color="success">Success</Button>
                                        <Button variant="contained" color="info">Info</Button>
                                        <Button variant="contained" color="warning">Warning</Button>
                                        <Button variant="contained" color="error">Error</Button>
                                    </Box>
                                </Box>
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Outlined Buttons
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Button variant="outlined">Default</Button>
                                        <Button variant="outlined" color="primary">Primary</Button>
                                        <Button variant="outlined" color="secondary">Secondary</Button>
                                        <Button variant="outlined" color="success">Success</Button>
                                        <Button variant="outlined" color="info">Info</Button>
                                        <Button variant="outlined" color="warning">Warning</Button>
                                        <Button variant="outlined" color="error">Error</Button>
                                    </Box>
                                </Box>
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Text Buttons
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Button variant="text">Default</Button>
                                        <Button variant="text" color="primary">Primary</Button>
                                        <Button variant="text" color="secondary">Secondary</Button>
                                        <Button variant="text" color="success">Success</Button>
                                        <Button variant="text" color="info">Info</Button>
                                        <Button variant="text" color="warning">Warning</Button>
                                        <Button variant="text" color="error">Error</Button>
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Button Sizes
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <Button variant="contained" size="small">Small</Button>
                                        <Button variant="contained" size="medium">Medium</Button>
                                        <Button variant="contained" size="large">Large</Button>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Form Fields Section */}
                        <Card sx={{ mb: 4 }}>
                            <CardContent sx={{ px: { xs: 2, sm: 3 } }}>
                                <Typography variant="h5" gutterBottom>
                                    Form Fields
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Text Input"
                                            placeholder="Enter text..."
                                            variant="outlined"
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            type="email"
                                            placeholder="email@example.com"
                                            variant="outlined"
                                            sx={{ mb: 2 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Password"
                                            type="password"
                                            placeholder="••••••••"
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Multiline Text"
                                            multiline
                                            rows={4}
                                            placeholder="Enter multiple lines of text..."
                                            variant="outlined"
                                            sx={{ mb: 2 }}
                                        />
                                        <FormControl fullWidth sx={{ mb: 2 }}>
                                            <FormLabel>Radio Group</FormLabel>
                                            <RadioGroup defaultValue="option1">
                                                <FormControlLabel value="option1" control={<Radio />} label="Option 1" />
                                                <FormControlLabel value="option2" control={<Radio />} label="Option 2" />
                                                <FormControlLabel value="option3" control={<Radio />} label="Option 3" />
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                <Box sx={{ mt: 3 }}>
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <FormLabel>Select Dropdown</FormLabel>
                                        <Select defaultValue="" label="Select an option">
                                            <MenuItem value="">Select an option</MenuItem>
                                            <MenuItem value="1">Option 1</MenuItem>
                                            <MenuItem value="2">Option 2</MenuItem>
                                            <MenuItem value="3">Option 3</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                        <FormControlLabel control={<Checkbox defaultChecked />} label="Checked Checkbox" />
                                        <FormControlLabel control={<Checkbox />} label="Unchecked Checkbox" />
                                        <FormControlLabel control={<Checkbox disabled />} label="Disabled Checkbox" />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Cards Section */}
                        <Card sx={{ mb: 4 }}>
                            <CardContent sx={{ px: { xs: 2, sm: 3 } }}>
                                <Typography variant="h5" gutterBottom>
                                    Cards
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={4}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Basic Card
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    This is a basic card with outlined variant.
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Card variant="elevation" elevation={3}>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Elevated Card
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    This card has elevation/shadow.
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Card sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Colored Card
                                                </Typography>
                                                <Typography variant="body2">
                                                    This card uses primary color background.
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Other Components Section */}
                        <Card sx={{ mb: 4 }}>
                            <CardContent sx={{ px: { xs: 2, sm: 3 } }}>
                                <Typography variant="h5" gutterBottom>
                                    Other Components
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Chips
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip label="Default" />
                                        <Chip label="Primary" color="primary" />
                                        <Chip label="Secondary" color="secondary" />
                                        <Chip label="Success" color="success" />
                                        <Chip label="Info" color="info" />
                                        <Chip label="Warning" color="warning" />
                                        <Chip label="Error" color="error" />
                                        <Chip label="Deletable" onDelete={() => {}} />
                                    </Box>
                                </Box>
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Alerts
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Alert severity="success">This is a success alert.</Alert>
                                        <Alert severity="info">This is an info alert.</Alert>
                                        <Alert severity="warning">This is a warning alert.</Alert>
                                        <Alert severity="error">This is an error alert.</Alert>
                                    </Box>
                                </Box>
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Progress Indicators
                                    </Typography>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" gutterBottom>
                                            Linear Progress
                                        </Typography>
                                        <LinearProgress variant="determinate" value={60} />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="body2">
                                            Circular Progress:
                                        </Typography>
                                        <CircularProgress size={24} />
                                        <CircularProgress size={24} color="secondary" />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Container>
                </Box>
                <Footer />
            </Box>
        </ThemeProvider>
    );
}

export default ThemePreview;
